<template>
  <section
    :class="[
      'flex min-w-0 flex-col rounded-theme-md border border-theme-border bg-theme-surface shadow-theme-card',
      compact ? 'p-3' : 'p-4',
    ]"
  >
    <!-- Header: title (+ subtitle) and "Bekijk alles" link. Skipped
         entirely when the consumer didn't provide a title AND no action
         slot/view-all link is present — used by the Mijn-omgeving sub-page
         routes, which surface their identity via the page-level
         StandardToolPage header above and don't want a duplicate
         heading inside the card. -->
    <header
      v-if="title || viewAllTo || $slots.action"
      class="section-card__header flex min-w-0 items-start justify-between gap-3 mb-2"
    >
      <div class="min-w-0 flex-1">
        <h3
          v-if="title"
          :class="[
            'section-card__title truncate font-semibold text-theme-text',
            compact ? 'text-sm' : 'text-base',
          ]"
        >
          {{ title }}
        </h3>
        <p
          v-if="subtitle"
          class="line-clamp-2 min-h-[2.0625rem] whitespace-normal break-words text-xs leading-snug text-theme-text-muted"
        >
          {{ subtitle }}
        </p>
      </div>

      <component
        v-if="viewAllTo || $slots.action"
        :is="viewAllTo ? 'router-link' : 'span'"
        :to="viewAllTo || undefined"
        class="section-card__action inline-flex shrink-0 items-center gap-1 text-xs font-medium text-theme-brand hover:text-theme-brand-hover"
      >
        <slot name="action">
          <span>{{ viewAllLabel || t('ui.section_view_all') }}</span>
          <icon :path="mdiArrowRight" size="14" />
        </slot>
      </component>
    </header>

    <!-- Body: slot, or fallback to EmptyState when empty -->
    <div class="flex-1 min-h-0 flex flex-col">
      <slot v-if="!isEmpty && !isLoading && !error" />
      <!-- A layout-matched skeleton wins over the generic loading EmptyState
           when the consumer provides one. Falls back to the existing spinner
           text when no custom skeleton is supplied so unmigrated callers
           keep their old behaviour. -->
      <template v-else-if="isLoading">
        <slot name="loadingSkeleton">
          <app-loading-skeleton
            variant="spinner"
            size="sm"
            :label="loadingTitle || t('ui.section_loading')"
          />
        </slot>
      </template>
      <app-error-state
        v-else-if="error"
        compact
        :title="errorTitle || t('ui.section_error')"
        :message="typeof error === 'string' ? error : ''"
      />
      <app-empty-state
        v-else
        compact
        container-class="flex-1"
        data-test="section-empty-state"
        :title="emptyTitle || t('ui.section_empty')"
        :message="emptyMessage"
      >
        <slot v-if="$slots.emptyAction" name="emptyAction" />
      </app-empty-state>
    </div>

    <!-- Optional footer (e.g. mini-summary, secondary action). -->
    <footer v-if="$slots.footer" class="mt-2 border-t border-theme-border pt-2">
      <slot name="footer" />
    </footer>
  </section>
</template>

<script setup>
  /* eslint-disable no-undef */
  import Icon from '@/components/Icon';
  import AppEmptyState from '@/components/ui/states/AppEmptyState.vue';
  import AppErrorState from '@/components/ui/states/AppErrorState.vue';
  import AppLoadingSkeleton from '@/components/ui/states/AppLoadingSkeleton.vue';
  import { mdiArrowRight } from '@mdi/js';
  import { useI18n } from 'vue-i18n';

  defineProps({
    title: { type: String, required: true },
    subtitle: { type: String, default: '' },
    viewAllTo: { type: [String, Object], default: null },
    viewAllLabel: { type: String, default: '' },
    isEmpty: { type: Boolean, default: false },
    isLoading: { type: Boolean, default: false },
    error: { type: [String, Boolean], default: null },
    emptyTitle: { type: String, default: '' },
    emptyMessage: { type: String, default: '' },
    loadingTitle: { type: String, default: '' },
    errorTitle: { type: String, default: '' },
    compact: { type: Boolean, default: false },
  });

  const { t } = useI18n();
</script>
